import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useI18n } from '@/hooks/useI18n';
import {
  useCreateEmailTemplate,
  useDeleteEmailTemplate,
  useEmailTemplatesQuery,
  useUpdateEmailTemplate,
} from '@/lib/admin/email-templates/hooks/useEmailTemplates';
import {
  EmailTemplate,
  EmailTemplateChannel,
  EmailTemplateFactory,
  EmailTemplatePayload,
} from '@/lib/admin/email-templates/models/email-template.model';
import {
  EmailTemplateSort,
  EmailTemplateSortKey,
  filterTemplates,
  sortTemplates,
} from '@/lib/admin/email-templates/utils/email-template-filters';
import { EmailTemplatesTable } from '@/lib/admin/email-templates/components/EmailTemplatesTable';
import {
  EmailTemplateModal,
  type EmailTemplateModalMode,
} from '@/lib/admin/email-templates/modals/EmailTemplateModal';

interface ModalState {
  mode: EmailTemplateModalMode;
  /** Chiave originale del record in modifica; null in creazione/duplicazione. */
  originalKey: string | null;
  initialValues: EmailTemplatePayload;
}

interface EmailTemplatesTabProps {
  /**
   * Canale da mostrare. Non è uno stato interno: i due tab "Emails" e
   * "Notifications" vivono nella TabsHeader di AdminDashboard insieme a
   * "Releases" e "Sistema", quindi il canale arriva da lì (e dall'URL `?tab=`).
   */
  channel: EmailTemplateChannel;
}

/**
 * Tab admin di gestione di `email_templates`.
 *
 * Un solo record pilota due canali (email e notifica in-app): cambiare canale non
 * filtra i dati, cambia soltanto quali colonne/campi del record si stanno
 * guardando. Ricerca e ordinamento sono client-side qui (la tabella è piccola e
 * viene caricata tutta in una volta): la tabella riceve righe già pronte.
 */
export const EmailTemplatesTab = ({ channel }: EmailTemplatesTabProps) => {
  const { t } = useI18n();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<EmailTemplateSort>({
    key: 'template_key',
    direction: 'asc',
  });
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const { data: templates = [], isLoading, isFetched } = useEmailTemplatesQuery();
  const createTemplate = useCreateEmailTemplate();
  const updateTemplate = useUpdateEmailTemplate();
  const deleteTemplate = useDeleteEmailTemplate();

  const handleSortChange = (key: EmailTemplateSortKey) => {
    setSort((previous) =>
      previous.key === key
        ? { key, direction: previous.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const visibleTemplates = useMemo(
    () => sortTemplates(filterTemplates(templates, search, channel), sort, channel),
    [templates, search, channel, sort]
  );

  const existingKeys = useMemo(
    () => templates.map((template) => template.template_key),
    [templates]
  );

  const openCreate = () =>
    setModalState({
      mode: 'create',
      originalKey: null,
      initialValues: EmailTemplateFactory.empty(),
    });

  const openEdit = (template: EmailTemplate) =>
    setModalState({
      mode: 'edit',
      originalKey: template.template_key,
      initialValues: EmailTemplateFactory.toPayload(template),
    });

  const openDuplicate = (template: EmailTemplate) =>
    setModalState({
      mode: 'create',
      originalKey: null,
      initialValues: EmailTemplateFactory.duplicate(template),
    });

  const handleSubmit = async (payload: EmailTemplatePayload) => {
    if (!modalState) return;
    try {
      if (modalState.mode === 'edit' && modalState.originalKey) {
        await updateTemplate.mutateAsync({ key: modalState.originalKey, payload });
      } else {
        await createTemplate.mutateAsync(payload);
      }
      setModalState(null);
    } catch {
      // Il toast di errore è già emesso dalla mutation: qui teniamo solo la
      // modale aperta, così l'admin non perde quanto scritto.
    }
  };

  const isFirstLoad = isLoading && !isFetched;
  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('emailTemplates.searchPlaceholder')}
            className="pl-9"
          />
        </div>

        <Button size="sm" onClick={openCreate} className="h-9 shrink-0 px-3">
          <Plus className="mr-2 h-4 w-4" />
          {t('emailTemplates.newTemplate')}
        </Button>
      </div>

      {/*
        La tabella scrolla al proprio interno (header sticky, nessuna paginazione) e
        prende tutta l'altezza residua: la catena flex/min-h-0 arriva da
        AdminDashboard, che per questi tab mette il layout in full-height.
      */}
      <div className="min-h-0 flex-1">
        {isFirstLoad ? (
          <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {Array.from({ length: 8 }, (_, index) => (
              <Skeleton key={index} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <EmailTemplatesTable
            templates={visibleTemplates}
            channel={channel}
            isLoading={isLoading}
            sort={sort}
            onSortChange={handleSortChange}
            onEdit={openEdit}
            onDuplicate={openDuplicate}
            onDelete={(key) => deleteTemplate.mutate(key)}
            emptyState={
              search.trim()
                ? t('emailTemplates.table.noResults')
                : t('emailTemplates.table.empty')
            }
          />
        )}
      </div>

      {modalState && (
        <EmailTemplateModal
          isOpen
          onClose={() => setModalState(null)}
          mode={modalState.mode}
          channel={channel}
          initialValues={modalState.initialValues}
          existingKeys={existingKeys}
          onSubmit={handleSubmit}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};

export default EmailTemplatesTab;
